import { useMemo } from 'react';
import { useAuth } from '@hooks/useAuth';
import { replaceUrlParams } from '@utils/string-util';
import endpoints from 'services/api/endpoints';
import useFetch from 'services/api/useFetch';

import type { BaseResult } from '../../../types/api';
import type {
  CommunicationsRequest,
  UserProfile,
  UserProfileRequest,
  UserRoleValue,
} from '../../../types/auth';

export const useAccountApi = () => {
  const { isAuthenticated, logout } = useAuth();
  const api = useFetch();
  return useMemo(() => {
    return {
      async availableRoles(): Promise<UserRoleValue[]> {
        return api.get(endpoints.user.availableRoles);
      },
      async get(id: string): Promise<UserProfile> {
        return api.get(replaceUrlParams(endpoints.accounts.get, { id }));
      },
      async update(params: UserProfileRequest): Promise<UserProfile> {
        return api.putFile(endpoints.accounts.update, params);
      },
      async updateCommunications(
        request: CommunicationsRequest
      ): Promise<BaseResult> {
        return api.patch(endpoints.accounts.communications, request);
      },
      async userInfo(): Promise<UserProfile> {
        try {
          return await api.get(endpoints.accounts.userInfo);
        } catch (error: unknown) {
          if (isAuthenticated) {
            logout();
          }

          return Promise.reject(error);
        }
      },
    };
  }, [api, isAuthenticated, logout]);
};

export const accountApiKeys = {
  all: ['account'] as const,
  get: (id: string) => [...accountApiKeys.all, id] as const,
  roles: () => [...accountApiKeys.all, 'account-roles'] as const,
};
